
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function GrottoBayPage() {
  useEffect(() => {
    document.title = 'Grotto Bay Painters Cape Town | Coastal Painting Services | Marine Grade Protection';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional coastal painting services in Grotto Bay, Cape Town. Specialized marine-grade coatings and weather protection for beachfront properties. Expert painters with coastal expertise.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Grotto Bay painters, coastal painting Cape Town, marine grade coatings');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/grotto-bay`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Grotto Bay Painters Cape Town",
      "description": "Professional coastal painting services in Grotto Bay, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/grotto-bay`,
      "areaServed": {
        "@type": "Place",
        "name": "Grotto Bay, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.8892,
          "longitude": 18.4756
        },
        "geoRadius": "8000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Grotto Bay Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/aa8b823d32b6535694f3aa75385ce210.jpg"
            alt="Professional coastal painting services in Grotto Bay, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Expert <span className="text-yellow-400">Grotto Bay Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Specialized coastal painting services for Grotto Bay properties. Marine-grade protection and expert craftsmanship designed to withstand harsh coastal conditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Specialized Coastal Painting Services in Grotto Bay
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert painting solutions designed specifically for Grotto Bay's unique coastal environment, providing superior protection against salt air and marine conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-smile-line',
                title: 'Beachfront Homes',
                description: 'Specialized painting for coastal homes with marine-grade coatings that resist salt air corrosion and UV damage.',
                features: ['Marine-grade primers', 'Salt air resistance', 'UV protection', 'Moisture barriers']
              },
              {
                icon: 'ri-water-percent-line',
                title: 'Waterproofing Solutions',
                description: 'Advanced waterproofing and protective coatings designed for properties exposed to ocean spray and humidity.',
                features: ['Waterproof membranes', 'Moisture control', 'Crack sealing', 'Preventive maintenance']
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Weather Protection',
                description: 'Comprehensive weather protection systems to safeguard your property against coastal storms and elements.',
                features: ['Storm damage prevention', 'Wind resistance', 'Rain protection', 'Long-term durability']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                        <i className={`${feature.icon} text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Grotto Bay Properties Trust Our Coastal Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Coastal Specialization',
                    description: 'Deep expertise in coastal painting challenges with proven solutions for salt air, humidity, and marine conditions.'
                  },
                  {
                    title: 'Marine-Grade Materials',
                    description: 'Premium marine-grade coatings and materials specifically designed for harsh coastal environments.'
                  },
                  {
                    title: 'Weather Resilience',
                    description: 'Advanced protective systems that withstand coastal storms, high winds, and extreme weather conditions.'
                  },
                  {
                    title: 'Local Knowledge',
                    description: 'Intimate understanding of Grotto Bay\'s unique microclimate and specific environmental challenges.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20coastal%20painters%20working%20on%20Grotto%20Bay%20beachfront%20property%2C%20marine-grade%20painting%20equipment%20and%20materials%2C%20ocean%20view%20property%20maintenance%2C%20specialized%20coastal%20painting%20techniques%20Cape%20Town&width=600&height=700&seq=grotto-bay-team-work&orientation=portrait"
                alt="Professional coastal painting in Grotto Bay"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Grotto Bay Coastal Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest coastal painting projects in Grotto Bay, showcasing our expertise in marine-grade protection and coastal resilience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Oceanfront Villa Restoration',
                location: 'Grotto Bay Beachfront',
                description: 'Complete exterior restoration with marine-grade coatings for a luxury beachfront villa.',
                image: 'https://readdy.ai/api/search-image?query=Oceanfront%20villa%20painting%20restoration%20in%20Grotto%20Bay%20Cape%20Town%2C%20luxury%20beachfront%20home%20with%20marine-grade%20coatings%2C%20coastal%20property%20with%20ocean%20views%2C%20professional%20exterior%20painting&width=400&height=300&seq=grotto-bay-project-1&orientation=landscape'
              },
              {
                title: 'Coastal Apartment Complex',
                location: 'Grotto Bay Heights',
                description: 'Comprehensive waterproofing and painting of a 6-unit coastal apartment complex.',
                image: 'https://readdy.ai/api/search-image?query=Coastal%20apartment%20complex%20painting%20in%20Grotto%20Bay%2C%20multi-unit%20residential%20building%20with%20waterproofing%2C%20seaside%20property%20maintenance%2C%20professional%20building%20painting%20Cape%20Town&width=400&height=300&seq=grotto-bay-project-2&orientation=landscape'
              },
              {
                title: 'Beach House Renovation',
                location: 'Grotto Bay Central',
                description: 'Interior and exterior renovation of a charming beach house with salt-resistant finishes.',
                image: 'https://readdy.ai/api/search-image?query=Beach%20house%20renovation%20painting%20in%20Grotto%20Bay%20Cape%20Town%2C%20charming%20coastal%20home%20with%20salt-resistant%20finishes%2C%20beachside%20property%20painting%2C%20traditional%20coastal%20architecture&width=400&height=300&seq=grotto-bay-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Grotto Bay Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied coastal property owners in Grotto Bay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Robert Chen',
                location: 'Grotto Bay Beachfront',
                rating: 5,
                text: 'Incredible work on our oceanfront villa. The marine-grade coatings have protected our home beautifully against the salt air. Exceptional coastal expertise!'
              },
              {
                name: 'Linda Botha',
                location: 'Grotto Bay Heights',
                rating: 5,
                text: 'Professional service from start to finish. They understood the unique challenges of coastal painting and delivered outstanding results. Highly recommended.'
              },
              {
                name: 'Michael Stevens',
                location: 'Grotto Bay Central',
                rating: 5,
                text: 'The waterproofing and painting work exceeded our expectations. Our beach house has never looked better and we have confidence in the long-term protection.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Protect Your Grotto Bay Coastal Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for specialized coastal painting consultation and marine-grade protection solutions. Safeguard your investment with expert coastal painting services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
